import { ActionButton } from "../../../components/Buttons";
import BackgroundLights from "./BackgroundLights";
import ChooseRefinedCardView from "./ChooseRefinedCardView";
import EmpathyPageHeader from "./EmpathyPageHeader";
import ShareCardPreviewView from "./ShareCardPreviewView";

function TranslationAccuracyDisclaimer() {
  return (
    <p className="relative z-40 mx-auto max-w-[21rem] px-2 text-center text-[11px] font-normal leading-snug text-white/46 min-[360px]:whitespace-nowrap sm:text-xs">
      AI generation can miss nuance. Review for accuracy.
    </p>
  );
}

function LivingWallActionButton({
  onAddToLivingWall,
  addingToWall,
  wallCardId,
  disabled = false,
}) {
  const livingWallLabel = wallCardId
    ? "Added to Living Wall"
    : "Add to Wall";

  return (
    <ActionButton
      type="button"
      onClick={onAddToLivingWall}
      disabled={addingToWall || disabled || Boolean(wallCardId)}
      fullWidth
      icon={wallCardId ? "check" : "wall"}
      size="md"
      variant="wall"
    >
      {addingToWall ? "Adding..." : livingWallLabel}
    </ActionButton>
  );
}

function RefineResultActions({
  isInteractionDisabled,
  onShare,
  onAddToLivingWall,
  isWallSubmissionEnabled,
  addingToWall,
  sharing,
  wallCardId,
}) {
  return (
    <div className="relative z-50 mx-auto flex w-full max-w-[250px] flex-col gap-3 sm:max-w-[315px]">
      {isWallSubmissionEnabled ? (
        <LivingWallActionButton
          onAddToLivingWall={onAddToLivingWall}
          addingToWall={addingToWall}
          disabled={isInteractionDisabled}
          wallCardId={wallCardId}
        />
      ) : null}

      <ActionButton
        type="button"
        onClick={onShare}
        disabled={sharing || isInteractionDisabled}
        className="whitespace-nowrap"
        fullWidth
        icon="share"
        size="md"
        variant="secondary"
      >
        {sharing ? "Preparing Your Link" : "Send Card"}
      </ActionButton>

      <p className="px-2 text-center text-[11px] font-semibold leading-snug text-white/54 sm:text-xs">
        Only this card is shared. Your original note stays private.
      </p>
    </div>
  );
}

function KeepCardIcon({ isAnonymous }) {
  const commonProps = {
    "aria-hidden": "true",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className: "h-4 w-4",
  };

  if (isAnonymous) {
    return (
      <svg {...commonProps}>
        <rect x="5" y="10" width="14" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
      <path d="M17 21v-8H7v8" />
      <path d="M7 3v5h8" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="m7 4 6 6-6 6" />
    </svg>
  );
}

function AccountResultCta({
  title,
  helper,
  isAnonymous,
  onClick,
}) {
  if (!title || typeof onClick !== "function") {
    return null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative z-50 mx-auto flex w-full max-w-[250px] items-center gap-3 rounded-xl border border-white/12 bg-white/[0.055] px-3.5 py-3 text-left shadow-[0_12px_32px_rgba(0,0,0,0.18)] backdrop-blur-md transition hover:border-[#f4d94e]/24 hover:bg-white/[0.08] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e] sm:max-w-[315px]"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#f4d94e]/20 bg-[#f4d94e]/10 text-[#fff1a1]">
        {isAnonymous ? <KeepCardIcon isAnonymous /> : <PlusIcon />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-black leading-5 text-white">
          {title}
        </span>
        {helper ? (
          <span className="mt-0.5 block text-xs font-semibold leading-5 text-white/58">
            {helper}
          </span>
        ) : null}
      </span>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-white/66">
        <ChevronIcon />
      </span>
    </button>
  );
}

function RefineResultView({
  cardVariants,
  selectedVariantId,
  onSelectVariant,
  result,
  rawThought,
  getVariantQuestion,
  getVariantTranslation,
  getVariantTranslations,
  authUser,
  onBackToPrompt,
  showBackButton,
  sharePreview,
  onSharePreviewMessageChange,
  onCloseSharePreview,
  onOpenShareSheet,
  onPrepareShareLink,
  onCopyShareLink,
  onShare,
  onToggleEditing,
  isEditEligible,
  isInteractionDisabled,
  translationStatus,
  onRetryTranslation,
  retryingTranslation,
  onAddToLivingWall,
  isWallSubmissionEnabled,
  addingToWall,
  sharing,
  wallCardId,
  accountCtaTitle,
  accountCtaHelper,
  isAccountCtaAnonymous,
  onAccountCtaClick,
  shareError,
  wallError,
  translationError,
  refineError,
}) {
  const hasGeneratedTranslation =
    Boolean(result?.translation?.trim()) ||
    cardVariants.some((variant) =>
      Boolean(getVariantTranslation?.(variant.id)?.trim())
    );
  const showTranslationDisclaimer = hasGeneratedTranslation;
  const visibleResultError = sharePreview
    ? wallError || translationError || refineError
    : shareError || wallError || translationError || refineError;

  const goBack = () => {
    if (sharePreview) {
      onCloseSharePreview();
      return;
    }

    onBackToPrompt();
  };

  return (
    <div className="relative mx-auto max-w-5xl animate-in fade-in slide-in-from-bottom-4 duration-700 text-center">
      <div className="absolute inset-x-0 top-[260px]">
        <BackgroundLights />
      </div>

      <div className="flex flex-col gap-3 sm:gap-4">
        <EmpathyPageHeader
          title="Your LettuceTalk Cards"
          authUser={authUser}
          onBack={goBack}
          showBackButton={showBackButton}
        />

        <ChooseRefinedCardView
          cardVariants={cardVariants}
          selectedVariantId={selectedVariantId}
          onSelectVariant={onSelectVariant}
          isInteractionDisabled={isInteractionDisabled}
          result={result}
          rawThought={rawThought}
          getVariantQuestion={getVariantQuestion}
          getVariantTranslation={getVariantTranslation}
          getVariantTranslations={getVariantTranslations}
          isEditEligible={isEditEligible}
          onEditCard={onToggleEditing}
        />

        <RefineResultActions
          isInteractionDisabled={isInteractionDisabled}
          onShare={onShare}
          onAddToLivingWall={onAddToLivingWall}
          isWallSubmissionEnabled={isWallSubmissionEnabled}
          addingToWall={addingToWall}
          sharing={sharing}
          wallCardId={wallCardId}
        />

        <AccountResultCta
          title={accountCtaTitle}
          helper={accountCtaHelper}
          isAnonymous={isAccountCtaAnonymous}
          onClick={onAccountCtaClick}
        />

        {showTranslationDisclaimer ? <TranslationAccuracyDisclaimer /> : null}

        <div className="min-h-[28px]">
          {wallCardId ? (
            <p className="text-center text-xs font-bold text-[#f4d94e]">
              Your card was added to the Living Wall.
            </p>
          ) : null}

          {visibleResultError ? (
            <p className="text-center text-sm font-bold text-red-600">
              {visibleResultError}
            </p>
          ) : null}

          {translationStatus === "needs_refresh" ? (
            <div className="mt-3 flex flex-col items-center gap-2">
              <p className="text-center text-sm font-bold text-[#f4d94e]">
                Your wording was saved, but the translation needs an update.
              </p>
              <ActionButton
                type="button"
                onClick={onRetryTranslation}
                disabled={isInteractionDisabled}
                size="sm"
                variant="darkSecondary"
              >
                {retryingTranslation ? "Updating translations…" : "Retry translation"}
              </ActionButton>
            </div>
          ) : null}
        </div>
      </div>

      {sharePreview ? (
        <ShareCardPreviewView
          selectedIntroId={sharePreview.selectedIntroId}
          cardUrl={sharePreview.cardUrl}
          isPreparing={sharePreview.isPreparing}
          preparationFailed={sharePreview.preparationFailed}
          shareError={shareError}
          onMessageChange={onSharePreviewMessageChange}
          onOpenShareSheet={onOpenShareSheet}
          onPrepareShareLink={onPrepareShareLink}
          onCopyLink={onCopyShareLink}
          onClose={onCloseSharePreview}
        />
      ) : null}
    </div>
  );
}

export default RefineResultView;
